import React, { useState, useEffect, useContext } from "react";
import { FaTimes } from "react-icons/fa";
import { UserContext } from "../context/UserContext";
import AddCharities from "../sections/AddCharities";

const CharityAdmin = ({ onClose }) => {
  const [charities, setCharities] = useState([]);
  const { emailId, isLoggedIn } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(null);
  const [showAdditionalMessagePopup, setShowAdditionalMessagePopup] = useState(null);
  const [additionalMessage, setAdditionalMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedCharityUserEmail, setSelectedCharityUserEmail] = useState("");
  const [editFormData, setEditFormData] = useState({
    project_title: "",
    category: "",
    description: "",
    image_url: null,
    target_amount: "",
    currency: "",
    end_date: ""
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchCharities = async () => {
      if (!isLoggedIn) return;
      setIsLoading(true);

      try {
        const apiEndpoint = emailId === "info@urcommunity.com.au"
          ? `/api/projectlist?type=charity`
          : `/api/projectlist?type=charity&email=${emailId}`;
        const response = await fetch(apiEndpoint);
        if (!response.ok) return;

        const data = await response.json();
        setCharities(data);
      } catch (error) {
        console.error("Error fetching charities:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCharities();
  }, [emailId, isLoggedIn]);

  const fetchCharityDetails = async (charityId) => {
    try {
      const response = await fetch(`/api/projectlist/${charityId}`);
      if (!response.ok) throw new Error("Failed to fetch charity details");

      const data = await response.json();
      setEditFormData({
        project_title: data.project_title,
        category: data.category,
        description: data.description,
        image_url: data.image_url,
        target_amount: data.target_amount,
        currency: data.currency,
        end_date: data.end_date
      });
    } catch (error) {
      console.error("Error fetching charity details:", error);
      alert("Failed to fetch charity details. Please try again.");
    }
  };

  const handleEdit = (charityId) => {
    setShowEditPopup(charityId);
    fetchCharityDetails(charityId);
  };

  const handleSaveChanges = async (updatedCharity) => {
    try {
      const response = await fetch(`/api/projectlist/${showEditPopup}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...updatedCharity,
          type: 'charity'  // Ensure type is set for update
        }),
      });
      if (!response.ok) throw new Error("Failed to update charity");

      // Refresh the charities list
      const updatedResponse = await fetch(
        emailId === "info@urcommunity.com.au"
          ? `/api/projectlist?type=charity`
          : `/api/projectlist?type=charity&email=${emailId}`
      );
      const updatedData = await updatedResponse.json();
      setCharities(updatedData);

      setShowEditPopup(null);
      setSuccessMessage("Charity updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes. Please try again.");
    }
  };

  const handleDelete = async (charityId) => {
    if (!window.confirm("Are you sure you want to delete this charity?")) return;

    try {
      const response = await fetch(`/api/projectlist/${charityId}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete charity");

      // Remove the deleted charity from the state
      setCharities(charities.filter(charity => charity.project_id !== charityId));
      setSuccessMessage("Charity deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting charity:", error);
      alert("Failed to delete charity. Please try again.");
    }
  };

  const notifyAdmin = async (charityId, userEmail, charityName) => {
    try {
      const response = await fetch(`/api/charity-admin-notification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          charityName,
          reason: additionalMessage,
          userEmail,
        }),
      });

      if (!response.ok) throw new Error("Failed to send admin notification");
      alert("Admin notification sent successfully!");
    } catch (error) {
      console.error("Error notifying admin:", error);
      alert("Failed to send admin notification. Please try again.");
    }
  };

  const handleAdminSubmit = async (charityId) => {
    try {
      const charityResponse = await fetch(`/api/charitieslist/${charityId}`);
      if (!charityResponse.ok) {
        throw new Error("Failed to fetch charity details");
      }
      const charityData = await charityResponse.json();

      await handleDelete(charityId);
      await notifyAdmin(charityId, charityData.email_id, charityData.name);

    } catch (error) {
      console.error("Error handling admin submit:", error);
    }
  };

  const getFilteredCharities = () => {
    const searchValue = searchTerm.toLowerCase().trim();
    if (!searchValue) return charities;

    return charities.filter((charity) =>
      [charity.name, charity.category, charity.description].some((field) =>
        field?.toString().toLowerCase().includes(searchValue)
      )
    );
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 999,
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "20px",
        width: "95%",
        maxWidth: "800px",
        maxHeight: "80vh",
        overflowY: "auto",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
        position: "relative",
      }}>
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}>
          <h3 style={{ margin: 0 }}>Charities Admin</h3>
          <FaTimes
            onClick={onClose}
            style={{
              cursor: "pointer",
              fontSize: "1.5rem",
              color: "#333",
            }}
          />
        </div>

        {/* Success Message */}
        {successMessage && (
          <div style={{
            textAlign: "center",
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "#d4edda",
            color: "#155724",
            borderRadius: "5px",
          }}>
            {successMessage}
          </div>
        )}

        {/* Search Bar */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
        }}>
          <input
            type="text"
            placeholder="Search charities..."
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              outline: "none",
            }}
            onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
          />
        </div>

        {/* Charities List */}
        {isLoading ? (
          <p style={{ textAlign: "center", color: "#555" }}>Loading charities...</p>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
            gap: "20px",
          }}>
            {getFilteredCharities().map((charity) => (
              <div key={charity.project_id} style={{
                backgroundColor: "white",
                padding: "15px",
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
              }}>
                <h3 style={{ 
                  margin: "0 0 10px 0",
                  fontSize: "16px",
                  fontWeight: "bold" 
                }}>
                  {charity.project_title}
                </h3>
                <p style={{ 
                  margin: "5px 0",
                  color: "#666",
                  fontSize: "14px" 
                }}>
                  Category: {charity.category}
                </p>
                <p style={{ 
                  margin: "5px 0",
                  color: "#666",
                  fontSize: "14px" 
                }}>
                  End Date: {new Date(charity.end_date).toLocaleDateString()}
                </p>
                
                {/* Progress Bar */}
                <div style={{ marginTop: "10px" }}>
                  <div style={{
                    height: "10px",
                    background: "#e0e0e0",
                    borderRadius: "5px",
                    overflow: "hidden"
                  }}>
                    <div style={{
                      height: "100%",
                      width: `${Math.min((charity.total_raised / charity.target_amount) * 100, 100)}%`,
                      background: "#ddd",
                      borderRadius: "5px"
                    }}/>
                  </div>
                  <p style={{ 
                    margin: "5px 0",
                    fontSize: "12px",
                    color: "#777" 
                  }}>
                    ${charity.total_raised || 0} raised of ${charity.target_amount}
                  </p>
                </div>

                <div style={{ 
                  display: "flex",
                  gap: "10px",
                  marginTop: "15px"
                }}>
                  <button
                    onClick={() => handleEdit(charity.project_id)}
                    style={{
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      flex: 1
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(charity.project_id)}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      flex: 1
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Charities Available */}
        {!isLoading && charities.length === 0 && (
          <p style={{ textAlign: "center", color: "#555" }}>
            No charities available. Click the "Add" button to create a new charity!
          </p>
        )}
      </div>

      {/* Edit Popup */}
      {showEditPopup && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            overflow: "auto"
          }}>
            <AddCharities
              charity={charities.find(c => c.project_id === showEditPopup)}
              onClose={() => setShowEditPopup(null)}
              handleSubmit={handleSaveChanges}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CharityAdmin;
